import React, { useState } from "react";
import {
    X,
    Building2,
    FolderKanban,
    Users2,
    Loader2,
    AlertCircle
} from "lucide-react";

const ENTITY_CONFIG = {
    organization: {
        title: "Create Organization",
        description: "Set up your organization to manage workspaces and team members.",
        icon: Building2,
        iconColor: "from-teal-400 to-teal-600",
        fields: [
            { id: "name", label: "Organization Name", placeholder: "Acme Lending Corp", required: true },
            { id: "description", label: "Description", placeholder: "Brief description of your organization", type: "textarea" }
        ]
    },
    workspace: {
        title: "Create Workspace",
        description: "Workspaces help organize teams and cases within your organization.",
        icon: FolderKanban,
        iconColor: "from-blue-400 to-blue-600",
        fields: [
            { id: "name", label: "Workspace Name", placeholder: "West Region Team", required: true },
            { id: "description", label: "Description", placeholder: "Brief description of this workspace", type: "textarea" }
        ]
    },
    group: {
        title: "Create Group",
        description: "Groups help organize members within a workspace for specific functions.",
        icon: Users2,
        iconColor: "from-purple-400 to-purple-600",
        fields: [
            { id: "name", label: "Group Name", placeholder: "Underwriters", required: true },
            { id: "description", label: "Description", placeholder: "Brief description of this group", type: "textarea" }
        ]
    }
};

const CreateEntityModal = ({ type, onClose, onSubmit, organization, workspace }) => {
    const config = ENTITY_CONFIG[type];
    const [formData, setFormData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState("");

    const handleChange = (fieldId, value) => {
        setFormData((prev) => ({ ...prev, [fieldId]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError("");

        // Validate required fields
        for (const field of config.fields) {
            if (field.required && !formData[field.id]?.trim()) {
                setError(`${field.label} is required.`);
                setIsSubmitting(false);
                return;
            }
        }

        try {
            await onSubmit(formData);
        } catch (err) {
            setError(err?.message || `Failed to create ${type}.`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const Icon = config.icon;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-slate-900 border dark:border-slate-800 border-slate-200 rounded-2xl w-full max-w-md shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-800 border-slate-200">
                    <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${config.iconColor} flex items-center justify-center`}>
                            <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold dark:text-white text-slate-900">{config.title}</h2>
                            <p className="text-xs dark:text-slate-400 text-slate-500">{config.description}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg dark:text-slate-400 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Context info for workspace/group */}
                    {type === "workspace" && organization && (
                        <div className="p-3 dark:bg-slate-800/50 bg-slate-50 rounded-lg text-sm">
                            <span className="dark:text-slate-400 text-slate-500">Organization: </span>
                            <span className="dark:text-white text-slate-900 font-medium">{organization.name}</span>
                        </div>
                    )}
                    {type === "group" && workspace && (
                        <div className="p-3 dark:bg-slate-800/50 bg-slate-50 rounded-lg text-sm">
                            <span className="dark:text-slate-400 text-slate-500">Workspace: </span>
                            <span className="dark:text-white text-slate-900 font-medium">{workspace.name}</span>
                        </div>
                    )}

                    {/* Dynamic Fields */}
                    {config.fields.map((field) => (
                        <div key={field.id}>
                            <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-1.5">
                                {field.label}
                                {field.required && <span className="text-red-400 ml-1">*</span>}
                            </label>
                            {field.type === "textarea" ? (
                                <textarea
                                    value={formData[field.id] || ""}
                                    onChange={(e) => handleChange(field.id, e.target.value)}
                                    placeholder={field.placeholder}
                                    rows={3}
                                    className="w-full px-4 py-2.5 dark:bg-slate-950 bg-slate-50 border dark:border-slate-700 border-slate-300 rounded-lg text-sm dark:text-white text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none resize-none"
                                />
                            ) : (
                                <input
                                    type="text"
                                    value={formData[field.id] || ""}
                                    onChange={(e) => handleChange(field.id, e.target.value)}
                                    placeholder={field.placeholder}
                                    className="w-full px-4 py-2.5 dark:bg-slate-950 bg-slate-50 border dark:border-slate-700 border-slate-300 rounded-lg text-sm dark:text-white text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
                                />
                            )}
                        </div>
                    ))}

                    {/* Error */}
                    {error && (
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-400">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 dark:bg-slate-800 bg-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 dark:text-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-500 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                `Create ${type.charAt(0).toUpperCase() + type.slice(1)}`
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateEntityModal;
